import { useState } from "react"
import { Head, router } from "@inertiajs/react"

import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { MainContainer } from "@/components/ui/container"
import { PageHeader } from "@/components/ui/headers"
import { StaffLayout } from "@/components/layouts/staff-layout"
import { ChannelHealth, type Channel } from "@/features/staff/channels/channel-health"
import { t } from "@/lib/i18n"

/**
 * Uber Eats, Rappi and DiDi Food.
 *
 * Health first, connection second — see `ChannelHealth` for why that order is
 * the whole feature.
 *
 * **The form takes a credential KEY, never a credential.** The token lives in
 * the secret store; this app holds a reference to it and nothing more, so there
 * is no field here that could carry a secret and no column behind it that could
 * store one. A restaurant with all three platforms connected gets no form at
 * all, rather than one that can only fail.
 */

type Props = {
  channels: Channel[]
  platforms: { value: string; label: string }[]
  unhealthyCount: number
  unresolvedCount: number
  timeZone: string
  errors?: Record<string, string[]>
}

function tChannels(key: string, options: Record<string, string | number> = {}) {
  return t(`ui.react.pages.staff.location.manage.channels.index.${key}`, options)
}

export default function StaffLocationManageChannelsIndex({
  channels,
  platforms,
  unhealthyCount,
  unresolvedCount,
  timeZone,
}: Props) {
  const [platform, setPlatform] = useState(platforms[0]?.value ?? "")
  const [storeId, setStoreId] = useState("")
  const [credentialKey, setCredentialKey] = useState("")

  const submit = () => {
    router.post(
      "/manage/channels",
      { platform, external_store_id: storeId, credential_key: credentialKey },
      { preserveScroll: true, onSuccess: () => { setStoreId(""); setCredentialKey("") } }
    )
  }

  return (
    <>
      <Head title={tChannels("page_title")} />

      <MainContainer>
        <PageHeader title={tChannels("page_title")} description={tChannels("page_description")} />

        <ChannelHealth
          channels={channels}
          unhealthyCount={unhealthyCount}
          unresolvedCount={unresolvedCount}
          timeZone={timeZone}
        />

        {platforms.length > 0 ? (
          <Card density="compact" data-testid="channel-connect">
            <CardHeader className="border-b">
              <CardTitle>{tChannels("connect_title")}</CardTitle>
            </CardHeader>

            <CardContent>
              <form
                className="flex flex-wrap items-end gap-2"
                onSubmit={(event) => {
                  event.preventDefault()
                  submit()
                }}
              >
                <label className="flex w-40 flex-col gap-1">
                  <span className="text-muted-foreground text-xs">{tChannels("platform_label")}</span>
                  <select
                    className="border-input h-11 rounded-md border px-3 text-sm md:h-9"
                    value={platform}
                    onChange={(event) => setPlatform(event.target.value)}
                    data-testid="channel-platform-select"
                  >
                    {platforms.map((option) => (
                      <option
                        key={option.value}
                        value={option.value}
                        data-testid={`channel-platform-option-${option.value}`}
                      >
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="flex min-w-40 flex-1 flex-col gap-1">
                  <span className="text-muted-foreground text-xs">{tChannels("store_id_label")}</span>
                  <input
                    className="border-input h-11 rounded-md border px-3 text-sm md:h-9"
                    value={storeId}
                    onChange={(event) => setStoreId(event.target.value)}
                    data-testid="channel-store-input"
                  />
                </label>

                <label className="flex min-w-40 flex-1 flex-col gap-1">
                  <span className="text-muted-foreground text-xs">
                    {tChannels("credential_key_label")}
                  </span>
                  <input
                    className="border-input h-11 rounded-md border px-3 text-sm md:h-9"
                    value={credentialKey}
                    onChange={(event) => setCredentialKey(event.target.value)}
                    data-testid="channel-credential-input"
                  />
                  {/* Said out loud on the screen, because an operator who thinks
                      this box wants a token will paste one. */}
                  <span className="text-muted-foreground text-xs">
                    {tChannels("credential_key_hint")}
                  </span>
                </label>

                <Button
                  type="submit"
                  className="h-11 md:h-9"
                  disabled={platform === "" || storeId.trim() === ""}
                  data-testid="channel-submit"
                >
                  {tChannels("connect")}
                </Button>
              </form>
            </CardContent>
          </Card>
        ) : null}
      </MainContainer>
    </>
  )
}

StaffLocationManageChannelsIndex.layout = (page: React.ReactNode) => (
  <StaffLayout>{page}</StaffLayout>
)
